import { useEffect, useState } from 'react';
import type { SupportedLanguage, LanguageConfig, CoreTranslationDictionary } from '@cubemeet/core/i18n';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_CONFIG,
  SUPPORTED_LANGUAGES,
  loadCoreTranslations,
  getCoreTranslationLoader,
} from '@cubemeet/core/i18n';
import { loadFromStorage, saveToStorage } from '@cubemeet/core/utils';

const STORAGE_KEY = 'cubemeet-language';
const EVENT_KEY = 'cubemeet-language-change';
const isBrowser = typeof window !== 'undefined';

export interface UseI18nResult {
  language: SupportedLanguage;
  config: LanguageConfig;
  translations: CoreTranslationDictionary | null;
  isLoading: boolean;
  setLanguage: (next: SupportedLanguage) => Promise<void>;
}

const getInitialLanguage = (): SupportedLanguage => {
  const stored = loadFromStorage<SupportedLanguage>(STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  if (isBrowser) {
    const navigatorLanguages = window.navigator.languages ?? [window.navigator.language];
    for (const language of navigatorLanguages) {
      const normalized = language as SupportedLanguage;
      if (SUPPORTED_LANGUAGES.includes(normalized)) {
        return normalized;
      }
    }
  }

  return DEFAULT_LANGUAGE;
};

export const useI18n = (): UseI18nResult => {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);
  const [translations, setTranslations] = useState<CoreTranslationDictionary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isBrowser) return;

    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<SupportedLanguage>;
      const newLang = customEvent.detail;
      if (newLang && newLang !== language) {
        setLanguageState(newLang);
      }
    };

    window.addEventListener(EVENT_KEY, handleLanguageChange);
    return () => {
      window.removeEventListener(EVENT_KEY, handleLanguageChange);
    };
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        const data = await loadCoreTranslations(language);
        if (!isMounted) return;
        setTranslations(data);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [language]);

  const setLanguage = async (next: SupportedLanguage) => {
    setIsLoading(true);
    const loader = getCoreTranslationLoader(next);
    try {
      const data = await loader();
      setTranslations(data);
      saveToStorage(STORAGE_KEY, next);
      
      if (isBrowser) {
        const event = new CustomEvent(EVENT_KEY, { detail: next });
        window.dispatchEvent(event);
      } else {
        setLanguageState(next);
      }
    } catch (error) {
      console.error('Failed to load translations for language', next, error);
      setLanguageState(DEFAULT_LANGUAGE);
      const fallbackData = await loadCoreTranslations(DEFAULT_LANGUAGE);
      setTranslations(fallbackData);
      saveToStorage(STORAGE_KEY, DEFAULT_LANGUAGE);
      
      if (isBrowser) {
        const event = new CustomEvent(EVENT_KEY, { detail: DEFAULT_LANGUAGE });
        window.dispatchEvent(event);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    language,
    config: LANGUAGE_CONFIG[language],
    translations,
    isLoading,
    setLanguage,
  };
};
